import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetClose } from "@/components/ui/sheet";
import { NAV } from "@/lib/site";

const Wordmark = () => (
  <Link to="/" className="group flex flex-col leading-none" data-testid="header-logo" aria-label="Porter New Orleans — home">
    <span className="font-serif text-[26px] sm:text-[28px] tracking-[0.32em] text-navy font-medium transition-colors duration-300 group-hover:text-gold-dark">
      PORTER
    </span>
    <span className="mt-1 text-[9px] tracking-[0.42em] text-gold-dark font-semibold">
      NEW ORLEANS
    </span>
  </Link>
);

export const Header = () => {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  return (
    <header className="sticky top-0 z-50 bg-white/85 backdrop-blur-xl border-b border-navy/10" data-testid="site-header">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 sm:px-8 py-4">
        <Wordmark />
        <nav className="hidden md:flex items-center gap-9" aria-label="Primary">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.label.toLowerCase()}`}
              className={({ isActive }) =>
                `relative text-[13px] font-semibold tracking-[0.14em] uppercase transition-colors duration-300 after:absolute after:-bottom-1.5 after:left-0 after:h-px after:bg-gold after:transition-all after:duration-300 ${
                  isActive ? "text-navy after:w-full" : "text-navy/55 hover:text-navy after:w-0 hover:after:w-full"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <Link
            to="/contact"
            data-testid="header-cta-button"
            className={`hidden md:inline-flex items-center rounded-full px-6 py-2.5 text-[13px] font-bold tracking-wide transition-all duration-300 ${
              pathname === "/contact"
                ? "bg-gold text-navy-dark"
                : "bg-navy text-cream hover:bg-navy-light hover:-translate-y-px"
            }`}
          >
            Get in Touch
          </Link>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                data-testid="mobile-menu-button"
                aria-label="Open menu"
                className="md:hidden inline-flex h-11 w-11 items-center justify-center rounded-full border border-navy/15 text-navy"
              >
                <Menu size={20} />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[85vw] bg-cream border-l border-navy/10 p-0">
              <div className="flex h-full flex-col px-8 pt-16 pb-10">
                <nav className="flex flex-col gap-2" aria-label="Mobile">
                  {NAV.map((item, i) => (
                    <SheetClose asChild key={item.to}>
                      <NavLink
                        to={item.to}
                        data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                        className={({ isActive }) =>
                          `font-serif text-4xl py-3 border-b border-navy/10 transition-colors duration-300 ${
                            isActive ? "text-gold-dark italic" : "text-navy"
                          }`
                        }
                        style={{ transitionDelay: `${i * 30}ms` }}
                      >
                        {item.label}
                      </NavLink>
                    </SheetClose>
                  ))}
                </nav>
                <div className="mt-auto">
                  <SheetClose asChild>
                    <NavLink
                      to="/contact"
                      data-testid="mobile-menu-cta"
                      className="inline-flex w-full items-center justify-center rounded-full bg-navy px-6 py-4 text-sm font-bold text-cream"
                    >
                      Get in Touch
                    </NavLink>
                  </SheetClose>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
